import { Button, Flex, FormLayout, Modal, statesByCountry, getCountryList, type CountryCode } from "@wayflyer/flyui";
import { RhfAutocompleteField, RhfDropdownField, RhfTextField } from "@wayflyer/flyui-hook-form";
import { FormProvider, useForm, useWatch, type UseFormReturn } from "react-hook-form";
import { useCallback, useEffect, useMemo, useRef } from "react";
import * as Yup from "yup";
import { yupResolver } from "@hookform/resolvers/yup";
import { useStateRegion, getJurisdictionalLabelGetters, type SupportedCountry } from "shared-ui/kyc";

import type { ParsedAddress, PlaceDetails } from "../../hooks/use-places-autocomplete/google-places";

import { useEditableAddressSearch, type EditableAddressFormValues } from "./use-editable-address-search";

interface FieldMapping {
  address_line_1: string;
  address_line_2: string;
  city: string;
  state: string;
  zip_code: string;
  country_code: string;
}

interface EditableAddressSearchFieldProps {
  form: UseFormReturn<any>;
  handleAddressSelect: (address: ParsedAddress, details: PlaceDetails) => void;
  label?: string;
  fieldName?: string;
  clearable?: boolean;
  countries?: CountryCode[];
  placeholder?: string;
  suppressInlineError?: boolean;
  parentFormError?: string;
  parentFormFieldName?: string;
  required?: boolean;
  onBlur?: () => void;
  onAddressChange?: (optionId: string | number | null) => void;
  fieldMapping?: FieldMapping;
}

const addressModalSchema = Yup.object().shape({
  country_code: Yup.string().required("Country is required"),
  address_line_1: Yup.string().required("Address line 1 is required"),
  address_line_2: Yup.string().nullable(),
  city: Yup.string().required("City is required"),
  state: Yup.string().required("State is required"),
  zip_code: Yup.string().required("ZIP code is required"),
});

export const EditableAddressSearchField = ({
  form,
  handleAddressSelect,
  label = "Address finder",
  placeholder = "Search for address",
  fieldName = "address_search",
  clearable = false,
  required = false,
  countries,
  parentFormFieldName,
  onBlur,
  onAddressChange,
  fieldMapping,
}: EditableAddressSearchFieldProps) => {
  const modalForm = useForm<EditableAddressFormValues>({
    resolver: yupResolver(addressModalSchema),
    defaultValues: {
      country_code: "US",
      address_line_1: "",
      address_line_2: "",
      city: "",
      state: "",
      zip_code: "",
    },
    mode: "all",
  });

  const handleSaveAddress = async () => {
    const isValid = await modalForm.trigger();
    if (!isValid) {
      return;
    }

    const values = modalForm.getValues();
    handleModalSave(values);
  };

  const handleAutocompleteBlur = () => {
    const searchTerm = addressAutocomplete.searchTerm;
    if (isOpeningManualEntryRef.current) {
      isOpeningManualEntryRef.current = false;
      form.clearErrors(fieldName);
      return;
    }

    if (!isOptionId && searchTerm) {
      form.setValue(fieldName, searchTerm, {
        shouldDirty: true,
      });
      form.clearErrors(fieldName);
    }

    // Call parent blur handler if provided
    onBlur?.();
  };

  const handleManualEntryClick = () => {
    isOpeningManualEntryRef.current = true;
    handleManualEntry(true);
  };

  const {
    addressAutocomplete,
    transformedOptions,
    handleAddressChange: internalHandleAddressChange,
    isManualEntryModalOpen,
    manualEntryAddress,
    handleManualEntry,
    handleModalSave,
    handleModalCancel,
    createableConfig,
  } = useEditableAddressSearch({
    form,
    handleAddressSelect,
    fieldName,
    countries,
    onManualEntry: handleManualEntryClick,
    fieldMapping,
    parentFormFieldName,
  });

  const handleAddressChange = useCallback(
    (optionId: string | number | null) => {
      if (optionId === null) {
        const currentSearchValue = form.getValues(fieldName);
        if (currentSearchValue) {
          internalHandleAddressChange(optionId);
          onAddressChange?.(optionId);
        } else {
          // on clear of the search field, also clear the address fields and the parent form field
          form.setValue(fieldName, "", { shouldDirty: true });
          if (fieldMapping) {
            form.resetField(fieldMapping.address_line_1);
            form.resetField(fieldMapping.address_line_2);
            form.resetField(fieldMapping.city);
            form.resetField(fieldMapping.state);
            form.resetField(fieldMapping.zip_code);
            form.resetField(fieldMapping.country_code);
          }
          if (parentFormFieldName) {
            form.setValue(parentFormFieldName, null, { shouldDirty: true, shouldValidate: true });
          }
        }
        return;
      }

      internalHandleAddressChange(optionId);
      onAddressChange?.(optionId);
    },
    [internalHandleAddressChange, onAddressChange, form, fieldName, fieldMapping, parentFormFieldName]
  );

  useEffect(() => {
    if (isManualEntryModalOpen && manualEntryAddress) {
      modalForm.reset({
        country_code: manualEntryAddress.country_code || "US",
        address_line_1: manualEntryAddress.address_line_1 || "",
        address_line_2: manualEntryAddress.address_line_2 || "",
        city: manualEntryAddress.city || "",
        state: manualEntryAddress.state || "",
        zip_code: manualEntryAddress.zip_code || "",
      });
    }
  }, [isManualEntryModalOpen, manualEntryAddress, modalForm]);

  const isOpeningManualEntryRef = useRef(false);

  const formFieldValue = useWatch({ control: form.control, name: fieldName });

  const isOptionId = useMemo(() => {
    if (!formFieldValue || typeof formFieldValue !== "string") {
      return false;
    }
    return transformedOptions.some((opt) => opt.id === formFieldValue);
  }, [transformedOptions, formFieldValue]);

  const optionsWithManualEntry = useMemo(() => {
    if (!formFieldValue || typeof formFieldValue !== "string" || isOptionId) {
      return transformedOptions;
    }

    return [
      {
        id: formFieldValue,
        text: formFieldValue,
        subText: "",
        icon: undefined,
      },
      ...transformedOptions,
    ];
  }, [transformedOptions, formFieldValue, isOptionId]);

  const manualEntryCountryOptions = useMemo(() => {
    return getCountryList(countries as CountryCode[]);
  }, [countries]);

  const countryCode = modalForm.watch("country_code");
  const countryStates = statesByCountry[countryCode] ?? [];
  const stateRegion = useStateRegion(countryCode as SupportedCountry);
  const stateLabel = stateRegion?.label || "State";

  const { getPostCodeLabel } = useMemo(() => getJurisdictionalLabelGetters(), []);
  const postalCodeLabel = getPostCodeLabel(countryCode as SupportedCountry);

  return (
    <>
      <Flex direction="column" gap="0" align="flex-start" minWidth="100%" maxWidth="100%">
        <RhfAutocompleteField
          form={form}
          required={required}
          name={fieldName}
          label={label}
          placeholder={placeholder}
          options={optionsWithManualEntry}
          externalOptions
          fetching={addressAutocomplete.fetching}
          onSearchTermChange={addressAutocomplete.onSearchTermChange}
          onChange={handleAddressChange}
          onBlur={handleAutocompleteBlur}
          clearable={clearable || undefined}
          size="large"
          createAction={createableConfig}
        />
        <Button
          variant="TertiaryAccent"
          onPointerDown={() => (isOpeningManualEntryRef.current = true)}
          onClick={handleManualEntryClick}
          type="button"
        >
          Enter manually
        </Button>
      </Flex>

      <Modal isOpen={isManualEntryModalOpen} onClose={handleModalCancel} size="normal" title="Enter address manually">
        <FormProvider {...modalForm}>
          <Flex justify="center" align="center" gap="6" direction="column" minWidth="100%">
            <Flex width="100%" direction="column" gap="4">
              <FormLayout>
                <RhfDropdownField
                  form={modalForm}
                  name="country_code"
                  label="Country"
                  options={manualEntryCountryOptions}
                />
                <RhfTextField form={modalForm} name="address_line_1" label="*Address line 1" />
                <RhfTextField form={modalForm} name="address_line_2" label="Address line 2" />
                <RhfTextField form={modalForm} name="city" label="*City" />
                {countryStates.length > 0 ? (
                  <RhfDropdownField form={modalForm} name="state" label={`*${stateLabel}`} options={countryStates} />
                ) : (
                  <RhfTextField form={modalForm} name="state" label={`*${stateLabel}`} />
                )}
                <RhfTextField form={modalForm} name="zip_code" label={`*${postalCodeLabel}`} />
              </FormLayout>
            </Flex>

            <Flex gap="2" width="100%">
              <Button variant="Secondary" onClick={handleModalCancel} fullWidth>
                Cancel
              </Button>
              <Button variant="Primary" onClick={handleSaveAddress} fullWidth>
                Save address
              </Button>
            </Flex>
          </Flex>
        </FormProvider>
      </Modal>
    </>
  );
};
